import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, setCsrfToken } from "./client";
import { meSchema, locationsResponseSchema, jobResponseSchema, resultsResponseSchema, usageSummarySchema, usageOwnersSchema, usageVisitsSchema, usageFeaturesSchema, usagePagesSchema, type Me, type LocationSummary, type JobResponse } from "../../api/contracts";

export const keys = {
  me: ["me"] as const,
  locations: ["locations"] as const,
  results: ["results"] as const,
  job: (id: string | null) => ["job", id] as const,
  rules: (scope: string, key: string | null) => ["rules", scope, key] as const,
  rulesHours: (scope: string, key: string | null) => ["rules-hours", scope, key] as const,
  actions: (query: string) => ["actions", query] as const,
  exclusions: (blvdId: string | null) => ["exclusions", blvdId] as const,
  staff: (blvdId: string | null, q: string) => ["exclusions", "staff", blvdId, q] as const,
  admin: {
    system: ["admin", "system"] as const, jobs: (status: string) => ["admin", "jobs", status] as const, events: ["admin", "events"] as const,
    users: ["admin", "users"] as const, directory: ["admin", "directory"] as const, webhooks: ["admin", "webhooks"] as const,
    admins: ["admin", "admins"] as const, apiClients: ["admin", "apiClients"] as const, health: ["admin", "health"] as const,
    analytics: (days: number) => ["admin", "analytics", days] as const,
    usage: {
      summary: (days: number) => ["admin", "usage", "summary", days] as const,
      owners: (days: number) => ["admin", "usage", "owners", days] as const,
      visits: (ownerId: string, days: number) => ["admin", "usage", "visits", ownerId, days] as const,
      features: ["admin", "usage", "features"] as const,
      pages: (days: number) => ["admin", "usage", "pages", days] as const,
    },
  },
};

/** null = signed out. Any other failure surfaces so the shell can show it. */
export function useMe() {
  return useQuery<Me | null>({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        const me = await api("/api/me", meSchema);
        setCsrfToken(me.csrfToken);
        return me;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) { setCsrfToken(null); return null; }
        throw e;
      }
    },
    staleTime: 60_000, retry: false,
  });
}

/**
 * Every page under the shell reads this (the rail's pulse comes from it), so it is fresh for 30s to
 * avoid a refetch per navigation and refetches every minute so the pulse keeps up with the planner.
 */
export function useLocations() {
  return useQuery<LocationSummary[]>({
    queryKey: keys.locations, queryFn: () => api("/api/locations", locationsResponseSchema).then((r) => r.locations),
    staleTime: 30_000, refetchInterval: 60_000,
  });
}

/** Owner Results page: one row per location the signed-in user can see. The numbers change once a day, so a few minutes of staleness is fine. */
export function useResults() {
  return useQuery({ queryKey: keys.results, queryFn: () => api("/api/results", resultsResponseSchema), staleTime: 5 * 60_000 });
}

const FINISHED = new Set(["done", "failed"]);
/** Polls a job until it finishes. Preview and smoke-test results live in `result`. */
export function useJob(id: string | null) {
  return useQuery<JobResponse>({
    queryKey: keys.job(id), enabled: id !== null,
    queryFn: () => api(`/api/jobs/${id}`, jobResponseSchema),
    refetchInterval: (q) => (q.state.data && FINISHED.has(q.state.data.status) ? false : 1500),
  });
}

export function useInvalidate() {
  const qc = useQueryClient();
  return async (...ks: readonly (readonly unknown[])[]) => { for (const k of ks) await qc.invalidateQueries({ queryKey: k }); };
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}

export function useUsageSummary(days: number) {
  return useQuery({ queryKey: keys.admin.usage.summary(days), queryFn: () => api(`/api/admin/usage/summary?days=${days}`, usageSummarySchema), placeholderData: keepPreviousData });
}
export function useUsageOwners(days: number) {
  return useQuery({ queryKey: keys.admin.usage.owners(days), queryFn: () => api(`/api/admin/usage/owners?days=${days}`, usageOwnersSchema), placeholderData: keepPreviousData });
}
/** Recent visits for one owner; only fetched while that owner's row is expanded. */
export function useOwnerVisits(ownerId: string | null, days: number) {
  return useQuery({
    queryKey: keys.admin.usage.visits(ownerId ?? "", days), enabled: ownerId !== null,
    queryFn: () => api(`/api/admin/usage/owners/${encodeURIComponent(ownerId!)}/visits?days=${days}`, usageVisitsSchema),
  });
}
export function useUsageFeatures() {
  return useQuery({ queryKey: keys.admin.usage.features, queryFn: () => api("/api/admin/usage/features", usageFeaturesSchema) });
}
export function useUsagePages(days: number) {
  return useQuery({ queryKey: keys.admin.usage.pages(days), queryFn: () => api(`/api/admin/usage/pages?days=${days}`, usagePagesSchema), placeholderData: keepPreviousData });
}
